import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, MapLocKeys } from '@cm/core/localization';
import { publicDPRoot } from '../../public-meta';

const dpRoot = `${publicDPRoot}|First Time Login`;

const sharedProps: Pick<LocDef, 'feature' | 'portal'> = {
	feature: HubFeature.CandidateAccessLogin,
	portal: 'CP',
};

export type FirstTimeLoginLoc =
	| 'CPFirstTimeLoginPasswordRequirements'
	| 'CPFirstTimeLoginTokenExpired'
	| 'CPFirstTimeLoginCandidateDataNotFound'
	| 'CPFirstTimeLoginRequestConfirmation'
	| 'CPFirstTimeLoginValidationDataNotFound'
	| 'CPFirstTimeLoginPasswordConfirmation'
	| 'CPFirstTimeLoginPageTitle'
	| 'CPFirstTimeLoginRegisterButton'
	| 'CPFirstTimeLoginLoggedInBefore'
	| 'CPFirstTimeLoginTextAbove'
	| 'CPFirstTimeLoginMethod'
	| 'CPFirstTimeLoginEmailMenuItem'
	| 'CPFirstTimeLoginScoreReportRegNumber'
	| 'CPFirstTimeLoginScoreReportTestId'
	| 'CPFirstTimeLoginLastName'
	| 'CPFirstTimeLoginLastNameRequired'
	| 'CPFirstTimeLoginRegistrationNumber'
	| 'CPFirstTimeLoginTestingId'
	| 'CPFirstTimeLoginUsernameError'
	| 'CPFirstTimeLoginEmailField'
	| 'CPFirstTimeLoginNewEmailField'
	| 'CPFirstTimeLoginEmailRequired'
	| 'CPFirstTimeLoginInvalidEmail'
	| 'CPFirstTimeLoginNewPasswordField'
	| 'CPFirstTimeLoginPasswordField'
	| 'CPFirstTimeLoginPasswordNameLogin'
	| 'CPFirstTimeLoginPasswordRequired'
	| 'CPFirstTimeLoginPasswordMinLength'
	| 'CPFirstTimeLoginPasswordMaxLength'
	| 'CPFirstTimeLoginPasswordsDontMatch'
	| 'CPFirstTimeLoginConfirmPassword'
	| 'CPFirstTimeLoginPasswordConfirmationRequired'
	| 'CPFirstTimeLoginSubmit'
	| 'CPFirstTimeLoginConfirmationMessage';

export const firstTimeLoginLocs: LocDefs<FirstTimeLoginLoc> = {
	CPFirstTimeLoginPasswordRequirements: {
		defaultValue: "Password doesn't meet our requirements",
		description: 'Error shown when a candidate has their name or login in their password.',
		displayPath: `${dpRoot}|Password Requirements`,
		displayType: 'Notification',
		...sharedProps,
	},
	CPFirstTimeLoginTokenExpired: {
		defaultValue: 'The link is expired or invalid, please start the process again',
		description:
			'The error text for when a first time login attempt has taken too long, and the associated token has expired when using the first time login page.',
		displayPath: `${dpRoot}|Token Expired`,
		displayType: 'Notification',
		...sharedProps,
	},
	CPFirstTimeLoginCandidateDataNotFound: {
		defaultValue:
			'If you have an account in our system you will get an email. If you do not receive an email shortly, please contact support.',
		description:
			'The error text for when unfound candidate data was submitted when using the first time login page.',
		displayPath: `${dpRoot}|Candidate Data Not Found`,
		displayType: 'Notification',
		...sharedProps,
	},
	CPFirstTimeLoginRequestConfirmation: {
		defaultValue:
			'An email has been sent to the provided email address. Follow the instructions in the email to proceed. Please allow several minutes for the email to arrive.',
		description:
			'The confirmation text for when a first time login request was completed successfully when using the first time login page.',
		displayPath: `${dpRoot}|Login Request Confirmation`,
		displayType: 'Content',
		...sharedProps,
	},
	CPFirstTimeLoginValidationDataNotFound: {
		defaultValue: 'Validation data not found',
		description:
			'The error text for when unfound validation data was submitted when using the first time login page.',
		displayPath: `${dpRoot}|Validation Data Not Found`,
		displayType: 'Content',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordConfirmation: {
		defaultValue: 'Password successfully set.',
		description:
			'The confirmation text for when a first time login password was successfully set when using the first time login page.',
		displayPath: `${dpRoot}|Password Confirmation`,
		displayType: 'Content',
		...sharedProps,
	},
	CPFirstTimeLoginPageTitle: {
		defaultValue: 'First Time Login',
		description: 'Page title for the first time login page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProps,
	},
	CPFirstTimeLoginRegisterButton: {
		defaultValue: 'Register',
		description: 'Button to take candidates to self registration.',
		displayPath: `${dpRoot}|Register`,
		displayType: 'Button',
		feature: HubFeature.CandidateAccessLoginSelfRegistration,
		portal: 'CP',
	},
	CPFirstTimeLoginLoggedInBefore: {
		defaultValue: 'Logged in before?',
		description:
			'The button text for the button that returns to the login page from the first time login page.',
		displayPath: `${dpRoot}|Logged In Before`,
		displayType: 'Button',
		...sharedProps,
	},
	CPFirstTimeLoginTextAbove: {
		defaultValue: '',
		description:
			'The text area above the first time login page, typically used for instructions or providing more information to candidates.',
		displayPath: `${dpRoot}|Text Above`,
		displayType: 'Content',
		htmlAllowed: true,
		...sharedProps,
	},
	CPFirstTimeLoginMethod: {
		defaultValue: 'Using',
		description: 'The field label text for the method selection for the first time login page.',
		displayPath: `${dpRoot}|Method`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginEmailMenuItem: {
		defaultValue: 'Email',
		description:
			'The text for the email menu option under the method select for the first time login page.',
		displayPath: `${dpRoot}|Email Menu Item`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginScoreReportRegNumber: {
		defaultValue: 'Score Report Registration Number',
		description:
			'The text for the score report registration number menu option under the method select for the first time login page.',
		displayPath: `${dpRoot}|Score Report Reg Number`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginScoreReportTestId: {
		defaultValue: 'Score Report Testing ID',
		description:
			'The text for the score report testing id menu option under the method select for the first time login page.',
		displayPath: `${dpRoot}|Score Report Test ID`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginLastName: {
		defaultValue: 'Last Name',
		description: 'The field label text for the last name field for the first time login page.',
		displayPath: `${dpRoot}|Last Name`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginLastNameRequired: {
		defaultValue: 'Please enter your last name',
		description:
			'The error text message that displays when the last name field is left blank for the first time login page.',
		displayPath: `${dpRoot}|Last Name Required`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginRegistrationNumber: {
		defaultValue: 'Registration Number',
		description:
			'The field label text for the registration number field for the first time login page.',
		displayPath: `${dpRoot}|Registration Number`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginTestingId: {
		defaultValue: 'Testing ID',
		description: 'The field label text for the testing id field for the first time login page.',
		displayPath: `${dpRoot}|Testing ID`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginUsernameError: {
		defaultValue: 'Please enter a value',
		description:
			'The error text message that displays when the username field is left blank for the first time login page.',
		displayPath: `${dpRoot}|Username Error`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginEmailField: {
		defaultValue: 'Email',
		description: 'The field label text for the email field for the first time login page.',
		displayPath: `${dpRoot}|Email Field`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginNewEmailField: {
		defaultValue: 'New Email',
		description: 'The field label text for the new email field for the first time login page.',
		displayPath: `${dpRoot}|New Email`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginEmailRequired: {
		defaultValue: 'Please enter your email',
		description:
			'The error text message that displays when the email or new email field is left blank for the first time login page.',
		displayPath: `${dpRoot}|Email Required`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginInvalidEmail: {
		defaultValue: 'Please use a valid email address',
		description:
			'The error text message that displays when the entered email is invalid in the email or new email field for the first time login page.',
		displayPath: `${dpRoot}|Invalid Email`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginNewPasswordField: {
		defaultValue: 'New Password',
		description:
			'The field label text for the new password field for the first time login page.',
		displayPath: `${dpRoot}|New Password Field`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordField: {
		defaultValue: 'Password',
		description: 'The field label text for the password field for the first time login page.',
		displayPath: `${dpRoot}|Password Field`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordNameLogin: {
		defaultValue: 'Password must not contain login or name',
		description: "Hint to show what is not allowed in the candidate's password.",
		displayPath: `${dpRoot}|Password Name Login`,
		displayType: 'Content',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordRequired: {
		defaultValue: 'Password is required',
		description:
			'The error message that displays when the password field is left blank when entering in a new password while using the first time login page.',
		displayPath: `${dpRoot}|Password Required`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordMinLength: {
		defaultValue: 'Password must contain at least 12 characters',
		description:
			"The error message that displays when the entered password doesn't meet the minimum 12 character count when entering in a new password while using the first time login page.",
		displayPath: `${dpRoot}|Password Min Length`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordMaxLength: {
		defaultValue: 'Password cannot contain more than 50 characters',
		description:
			"The error message that displays when the entered password doesn't meet the maximum 50 character count when entering in a new password while using the first time login page.",
		displayPath: `${dpRoot}|Password Max Length`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordsDontMatch: {
		defaultValue: 'Passwords do not match',
		description:
			'The error message that displays when the entered passwords do not match when entereing in a new password while using the first time login page.',
		displayPath: `${dpRoot}|Passwords Dont Match`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginConfirmPassword: {
		defaultValue: 'Confirm Password',
		description:
			'The confirm password field label that shows on the login while using the first time login page.',
		displayPath: `${dpRoot}|Confirm Password`,
		displayType: 'Label',
		...sharedProps,
	},
	CPFirstTimeLoginPasswordConfirmationRequired: {
		defaultValue: 'Confirm Password is required',
		description: 'Error to show the input is required.',
		displayPath: `${dpRoot}|Confirmation Required`,
		displayType: 'Error',
		...sharedProps,
	},
	CPFirstTimeLoginSubmit: {
		defaultValue: 'Submit',
		description: 'The button text for the submit button for the first time login page.',
		displayPath: `${dpRoot}|Submit`,
		displayType: 'Button',
		...sharedProps,
	},
	CPFirstTimeLoginConfirmationMessage: {
		defaultValue:
			'If the information provided matches our records, an email will be sent to the provided email address. If you do not receive an email, please contact customer support.',
		description: 'The message confirming the first time login form was submitted.',
		displayPath: `${dpRoot}|Confirmation Message`,
		displayType: 'Content',
		htmlAllowed: true,
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<FirstTimeLoginLoc>(firstTimeLoginLocs);
